import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { getParashaForDate } from '../utils/utils';

const Home = () => {
  const navigate = useNavigate();
  const { books, setSelectedBook, setSelectedParsha, searchQuery, setSearchQuery } = useAppContext();
  const [parsha, setParsha] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    async function fetchCurrentParsha() {
      const currentParsha = await getParashaForDate(new Date());
      setParsha(currentParsha);
    }
    fetchCurrentParsha();
  }, []);

  const handleFeaturedClick = () => {
    if (!parsha) return;
    if (parsha.isConnected){
      const currentBook = books.find(b => b.key === parsha.book.book_transliterated);
      setSelectedBook(currentBook);
      if (parsha.firstParsha) setSelectedParsha(parsha.firstParsha);
      navigate(`/book/${currentBook.id}/parsha/${encodeURIComponent(parsha.firstParsha || '')}`);
    }
    else{
      const currentBook = books.find(b => b.key === parsha.book.book_transliterated);
      if (currentBook) {
        const currentParsha = currentBook.parshiot?.find(p => p.name.trim() === parsha.hebrew.trim());
        setSelectedBook(currentBook);
        if (currentParsha) setSelectedParsha(currentParsha);
        navigate(`/book/${currentBook.id}/parsha/${encodeURIComponent(currentParsha?.name || '')}`);
      }
    }
  };

  const handleBookClick = (book) => {
    setSelectedBook(book);
    navigate(`/book/${book.id}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery)}&page=1`);
  };

  return (
    /* Removed bg-slate-900 to keep your original background */
    <div className="min-h-[100dvh] w-full flex flex-col">
      
      {/* TOP NAV BAR */}
      <div dir="rtl" className="w-full py-4 px-6 flex justify-between items-center" >
      
      {/* Contact */}
      <div className="relative">
        <button 
          onClick={() => setIsContactOpen(!isContactOpen)}
          className="flex items-center gap-2 text-amber-300 hover:text-amber-400 transition font-semibold text-base"
        >
          <span>צור קשר</span>
          <span className={`text-[11px] transition-transform ${isContactOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {isContactOpen && (
          <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl p-2 z-50 text-gray-800 border border-gray-200">
            <a href="mailto:tora2338@gmail.com" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
              <span className="text-xl">📧</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm">דברו איתנו במייל</span>
                <span className="text-[11px] text-gray-500">להערות, הארות, לקבלת הדף השבועי במייל הקדשה לעילוי נשמת וכו׳</span>
              </div>
            </a>
            <a  
              href="https://links.payboxapp.com/iBta1etlyWb" 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg transition border-t mt-1"
            >
              <span className="text-xl">💰</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm text-orange-700">תרומה בפייבוקס</span>
                <span className="text-[11px] text-gray-500">עזרו לנו לגדול</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Logo / Title center */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center hidden md:block">
        <span className="text-amber-300 font-bold text-lg md:text-xl tracking-wide">📖 הלכה בפרשה</span>
      </div>

      {/* App Link */}
      <a 
        href="https://play.google.com/store/apps/details?id=com.halachaparasha.app" 
        target="_blank" 
        rel="noreferrer" 
        className="font-semibold text-base text-amber-300 hover:text-amber-400 transition"
      >
        מעבר לאפליקציה 📱
      </a>
      </div>

      {/* COMPACTED CONTENT SECTION */}
      <div className="pt-6 card-container">
        {/* Restored size slightly: scale-95 feels much better than 90 */}
        <div onClick={handleFeaturedClick} className="card cursor-pointer transform scale-95 transition-transform hover:scale-100">
          <div className="flex flex-col items-center space-y-2">
            <h3 className="text-sm md:text-base text-gray-600 font-normal">פרשת השבוע</h3>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              פרשת {parsha?.hebrew || 'השבוע'}
            </h2>
            <div className="w-16 h-[2px] bg-gray-300 my-1"></div>
            <p className="text-sm text-gray-700 flex items-center gap-2">
              <span>👆</span>
              <span>לחץ כאן לקריאת פרשת השבוע</span>
            </p>
          </div>
        </div>
      </div>

<div className="h-8"></div>

{/* Search Bar */}
<form onSubmit={handleSearch} dir="rtl" className="w-full max-w-2xl mx-auto flex gap-2 px-6">
  <input
    type="text"
    placeholder="חפש בפרשות ובתכנים..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="flex-1 py-3 px-4 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
  />
  <button type="submit" className="px-6 py-3 bg-amber-400 text-white font-bold rounded-xl hover:bg-amber-500 transition shadow-md">
    חפש
  </button>
</form>

<div className="h-10"></div>

{/* Book Grid - Balanced Padding */}
<div dir="rtl" className="grid grid-cols-2 md:grid-cols-3 gap-6 w-full max-w-6xl mx-auto px-6">
  {books.map((book) => (
    <button
      key={book.id}
      onClick={() => handleBookClick(book)}
      /* Increased padding to !py-10 (approx 40px) to give it more weight */
      className="card cursor-pointer !py-10 !px-4 !min-h-0" 
    >
      <h3 className="text-xl md:text-3xl font-bold text-gray-800 text-center">{book.name}</h3>
    </button>
  ))}
</div>

      <div className="h-20"></div>
    </div>
  );
};

export default Home;